import { useMemo } from 'react';
import { Box, Divider, Stack, Typography } from '@mui/material';
import type { EventMediaSettingsDto, EventMediaSettingsPatchDto } from '@/types/dtos/media/media-settings.dto';
import type { NonSlotFlowPlaylistScope } from '@/types/dtos/media/flow-playlist-scope.enum';
import { NON_SLOT_FLOW_PLAYLIST_SCOPES } from '@/types/dtos/media/flow-playlist-scope.enum';
import MediaSettingsSwitchRow, { type MediaSettingsSwitchConfirm } from '@/components/media/media-settings-switch-row';
import EventVirtualMediaMatrixRow from '@/components/media/event-virtual-media-matrix-row';
import { useTranslations } from '@/i18n/use-translations';
import {
  isInfoOverlayComposite,
  useGetCompositeComponentsQuery,
} from '@/state/queries/composite-components/use-get-composite-components';
import type { ApplicableVirtualMediaDto } from '@/types/dtos/media/virtual-media.dto';
import type { MediaSettingsConfirmKey } from '@/components/media/media-settings-confirm.types';
import {
  compareCompositesByScopeSort,
  getVirtualMediaScopeSortKey,
  resolveCompositeSortOrder,
} from '@/helpers/virtual-media-scope-sort.helper';
import { compositeMatchesPlaylistContext } from '@/helpers/composite-matches-playlist-context.helper';

const SLOT_CONTENT_TYPES = ['Break', 'Session'] as const;

const FLOW_SCOPE_LABEL_KEYS: Record<NonSlotFlowPlaylistScope, string> = {
  pre_flow: 'media.virtualSettings.matrix.preFlow',
  flow_intro: 'media.virtualSettings.matrix.flowIntro',
  flow_transition: 'media.virtualSettings.matrix.flowTransition',
  flow_resume: 'media.virtualSettings.matrix.flowResume',
  flow_outro: 'media.virtualSettings.matrix.flowOutro',
  after_flow: 'media.virtualSettings.matrix.afterFlow',
};

function filterApplicableForFlowScope(
  composites: {
    slug: string;
    name: string;
    allowedIn?: string[];
    applicability?: Record<string, unknown>;
    sortOrderByScope?: Record<string, number>;
  }[],
  scope: NonSlotFlowPlaylistScope | 'slot',
  slotContentType?: string,
): ApplicableVirtualMediaDto[] {
  const sortKey = getVirtualMediaScopeSortKey(scope, slotContentType);
  const scopeKey = scope === 'slot' ? 'slot' : scope;

  return composites
    .filter((c) =>
      compositeMatchesPlaylistContext(
        {
          allowedIn: c.allowedIn as ('event' | 'flow' | 'break' | 'session')[] | undefined,
          applicability: c.applicability as
            | { flowScopes?: string[]; sessionTypes?: string[]; slotContentTypes?: string[] }
            | undefined,
        },
        {
          parentType: 'flow',
          scope,
          slotContentType,
        },
      ),
    )
    .sort((a, b) => compareCompositesByScopeSort(a, b, sortKey, scopeKey))
    .map((c) => ({
      slug: c.slug,
      name: c.name,
      sortOrder: resolveCompositeSortOrder(c.sortOrderByScope, sortKey, scopeKey),
      isClosing: c.slug.endsWith('-closing'),
    }));
}

type EventVirtualMediaSettingsSectionProps = {
  draft: EventMediaSettingsDto;
  canEdit: boolean;
  isPending: boolean;
  virtualFlowOverrideConfirm?: MediaSettingsSwitchConfirm | null;
  virtualSessionOverrideConfirm?: MediaSettingsSwitchConfirm | null;
  onRequestChange: (
    patch: EventMediaSettingsPatchDto,
    confirmKey?: MediaSettingsConfirmKey,
    fieldId?: 'virtualMedia.allowFlowOverride' | 'virtualMedia.allowSessionOverride',
  ) => void;
};

export default function EventVirtualMediaSettingsSection({
  draft,
  canEdit,
  isPending,
  virtualFlowOverrideConfirm,
  virtualSessionOverrideConfirm,
  onRequestChange,
}: EventVirtualMediaSettingsSectionProps) {
  const t = useTranslations();
  const { data: composites = [] } = useGetCompositeComponentsQuery();

  const virtual = draft.virtualMedia ?? {
    allowFlowOverride: true,
    allowSessionOverride: true,
    flowScopes: {
      pre_flow: { enabledSlugs: ['event-info'] },
      flow_intro: { enabledSlugs: ['flow-info'] },
      flow_transition: { enabledSlugs: ['flow-info'] },
      flow_resume: { enabledSlugs: ['flow-info'] },
      flow_outro: { enabledSlugs: ['flow-info'] },
      after_flow: { enabledSlugs: ['event-info'] },
    },
    slotByContentType: {
      Break: { enabledSlugs: ['break-info'] },
    },
  };

  const compositeSource = useMemo(
    () =>
      composites
        .filter((c) => !isInfoOverlayComposite(c))
        .map((c) => ({
          slug: c.slug,
          name: c.name,
          allowedIn: c.allowedIn,
          applicability: c.applicability as Record<string, unknown> | undefined,
          sortOrderByScope: c.sortOrderByScope,
        })),
    [composites],
  );

  const patchFlowScopeSlug = (scope: NonSlotFlowPlaylistScope, slug: string, enabled: boolean) => {
    const current = virtual.flowScopes?.[scope]?.enabledSlugs ?? [];
    const next = enabled ? [...new Set([...current, slug])] : current.filter((s) => s !== slug);
    onRequestChange({
      virtualMedia: {
        flowScopes: {
          [scope]: { enabledSlugs: next },
        },
      },
    });
  };

  const patchSlotSlug = (contentType: string, slug: string, enabled: boolean) => {
    const current = virtual.slotByContentType?.[contentType]?.enabledSlugs ?? [];
    const next = enabled ? [...new Set([...current, slug])] : current.filter((s) => s !== slug);
    onRequestChange({
      virtualMedia: {
        slotByContentType: {
          [contentType]: { enabledSlugs: next },
        },
      },
    });
  };

  return (
    <Box>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" gutterBottom>
        {t('media.virtualSettings.event.title')}
      </Typography>
      <Stack spacing={1} sx={{ mb: 2 }}>
        <MediaSettingsSwitchRow
          switchKey={`virtual-allowFlowOverride-${virtual.allowFlowOverride}`}
          label={t('media.virtualSettings.event.allowFlowOverride')}
          checked={virtual.allowFlowOverride}
          disabled={!canEdit || isPending}
          confirm={virtualFlowOverrideConfirm}
          onChange={(v) =>
            onRequestChange(
              { virtualMedia: { allowFlowOverride: v } },
              !v ? 'virtualMedia.allowFlowOverride.off' : undefined,
              !v ? 'virtualMedia.allowFlowOverride' : undefined,
            )
          }
        />
        <MediaSettingsSwitchRow
          switchKey={`virtual-allowSessionOverride-${virtual.allowSessionOverride}`}
          label={t('media.virtualSettings.event.allowSessionOverride')}
          checked={virtual.allowSessionOverride}
          disabled={!canEdit || isPending}
          confirm={virtualSessionOverrideConfirm}
          onChange={(v) =>
            onRequestChange(
              { virtualMedia: { allowSessionOverride: v } },
              !v ? 'virtualMedia.allowSessionOverride.off' : undefined,
              !v ? 'virtualMedia.allowSessionOverride' : undefined,
            )
          }
        />
      </Stack>
      <Typography variant="subtitle2" gutterBottom>
        {t('media.virtualSettings.matrix.title')}
      </Typography>
      <Stack spacing={2} divider={<Divider flexItem />}>
        {NON_SLOT_FLOW_PLAYLIST_SCOPES.map((scope) => (
          <EventVirtualMediaMatrixRow
            key={scope}
            label={t(FLOW_SCOPE_LABEL_KEYS[scope])}
            template={virtual.flowScopes?.[scope]}
            applicable={filterApplicableForFlowScope(compositeSource, scope)}
            canEdit={canEdit}
            disabled={isPending}
            onToggleSlug={(slug, enabled) => patchFlowScopeSlug(scope, slug, enabled)}
          />
        ))}
        {SLOT_CONTENT_TYPES.map((contentType) => (
          <EventVirtualMediaMatrixRow
            key={contentType}
            label={t(`media.virtualSettings.matrix.slot${contentType}`)}
            template={virtual.slotByContentType?.[contentType]}
            applicable={filterApplicableForFlowScope(compositeSource, 'slot', contentType)}
            canEdit={canEdit}
            disabled={isPending}
            onToggleSlug={(slug, enabled) => patchSlotSlug(contentType, slug, enabled)}
          />
        ))}
      </Stack>
    </Box>
  );
}
